<script setup lang="ts">
import { computed, ref } from 'vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { REPLACE_NAME_VARIABLES } from '@/libs/utilities/replaceVariable'

defineProps<{
  message: string
  selections: string[]
  maybeOtherUserNames: string[]
}>()

const emit = defineEmits<{
  close: []
  confirm: []
}>()

const hasPlannerKnowPlaceholder = ref(false)
const hasPlannerKnowRisks = ref(false)
const hasPlannerKnowResponsibilityForMissFalseDetectionReport = ref(false)

const couldSendMessage = computed(() => {
  return (
    hasPlannerKnowPlaceholder.value &&
    hasPlannerKnowRisks.value &&
    hasPlannerKnowResponsibilityForMissFalseDetectionReport.value
  )
})
</script>

<template>
  <div :class="$style.wrapper">
    <p :class="$style.title">
      別のユーザーの名前が誤記入されている可能性があります。
    </p>
    <div :class="$style.description">
      <p :class="$style.description__text">
        {{
          REPLACE_NAME_VARIABLES.name
        }}と記述すると自動でユーザー名に変換される仕組みがあります。<br />
        上記プレースホルダーで対応できる場合は、そちらを利用してください。
      </p>
      <p :class="$style.description__text">
        もしユーザー名ではない部分が引っかかっていた場合、<br />エンジニアにSlackか何かで報告いただけると助かります。
      </p>
    </div>
    <section :class="[$style.wrapper__section, $style.section]">
      <p :class="$style.section__title">以下のユーザー名が検出されました</p>
      <ul :class="$style.section__body">
        <li
          :class="$style.listItem"
          v-for="(name, index) in maybeOtherUserNames"
          :key="index"
        >
          {{ name }}
        </li>
      </ul>
    </section>
    <section :class="[$style.wrapper__section, $style.section]">
      <p :class="$style.section__title">送信予定のメッセージ本文</p>
      <div :class="[$style.section__body, $style.preview]">
        <p>{{ message }}</p>
        <ul>
          <li
            :class="$style.listItem"
            v-for="(selection, index) in selections"
            :key="index"
          >
            {{ selection }}
          </li>
        </ul>
      </div>
    </section>
    <p>
      メッセージ送信の取り消しはできません。<br />
      本当にこの内容で送信しますか？
    </p>
    <section>
      <label :class="$style.checkItem">
        <DolphinCheckbox v-model="hasPlannerKnowPlaceholder" />{{
          REPLACE_NAME_VARIABLES.name
        }}のプレースホルダーを利用すべきだとわかった
      </label>
      <label :class="$style.checkItem">
        <DolphinCheckbox v-model="hasPlannerKnowRisks" />
        個人情報漏洩の問題は無いことを確認した
      </label>
      <label :class="$style.checkItem">
        <DolphinCheckbox
          v-model="hasPlannerKnowResponsibilityForMissFalseDetectionReport"
        />
        検出ミスがある場合、エンジニアに報告することを了承した
      </label>
    </section>

    <section :class="$style.buttonSection">
      <FlatButton variant="secondary" @click="emit('close')"
        >送信をキャンセルして修正する</FlatButton
      >
      <FlatButton
        variant="caution"
        @click="emit('confirm')"
        :disabled="!couldSendMessage"
        >ユーザー名が誤記入されていそうとわかって送信する</FlatButton
      >
    </section>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 28px;
  align-items: center;
  outline: 8px solid $color-caution-border;
  border-radius: $border-radius-v2-base;

  &__section {
    align-self: start;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.title {
  font-size: 24px;
  font-weight: 700;
  color: $color-caution-accent;
}

.listItem {
  list-style: disc inside;
}

.description {
  &__text {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }

  &__list {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.section {
  width: 100%;

  &__title {
    font-size: 16px;
    font-weight: 700;
  }

  &__body {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.preview {
  width: 100%;
  padding: 16px;
  background-color: $color-surface-secondary;
  border-radius: $border-radius-v2-base;
}

.checkItem {
  display: flex;
  align-items: center;
  gap: 8px;
}

.buttonSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
}
</style>
